import {
    useLayoutEffect,
    useMemo,
    useRef,
    type FC,
    useId,
    memo
} from "react";
import {
    Animated,
    Platform,
    View
} from "react-native";
import type IDataTableContentProps from "./type";
import stylesheet, {
    DATA_TABLE_SKELETON_ROW_COUNT,
    DATA_TABLE_SKELETON_WIDTHS,
    getDataTableColumnProps,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useMotionTiming
} from "../../../../helpers/motion";
import {
    useDataTableSelectionContext,
    useDataTableContext
} from "../../context";
import {
    TABLE_BODY_VARIANTS
} from "../../../table/stylesheet";
import DataTableColumnHeader from "../columnHeader";
import DataTableSelectColumn from "../selectColumn";
import DataTableCards from "../../internal/cards";
import DataTableState from "../../internal/state";
import DataTableRow from "../../internal/row";
import Skeleton from "../../../skeleton";
import Alert from "../../../alert";
import Table from "../../../table";

const DataTableContent: FC<IDataTableContentProps> = ({
    customTheme: customThemeProp,
    style
}) => {
    const context = useDataTableContext({
        part: "Content"
    });

    const selectionContext = useDataTableSelectionContext();

    const baseId = useId();

    const {
        spaces
    } = NCoreUIKitTheme.useContext(customThemeProp ?? context?.customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(context?.customLocalize);

    const {
        notice: noticeDynamicStyle,
        select: selectDynamicStyle
    } = useStyles({
        selectWidth: context?.selectWidth ?? 0,
        spaces
    });

    const selectedSet = useMemo(() => new Set(selectionContext?.selectedRowIds), [
        selectionContext?.selectedRowIds
    ]);

    const sortTiming = useMotionTiming("hover", customThemeProp ?? context?.customTheme, "fade");

    const rowsOpacity = useRef(new Animated.Value(1)).current;

    const isSortMountedRef = useRef(false);

    const sortingKey = JSON.stringify(context?.sorting ?? []);

    useLayoutEffect(() => {
        if(!isSortMountedRef.current) {
            isSortMountedRef.current = true;

            return;
        }

        rowsOpacity.setValue(0);

        const animation = Animated.timing(rowsOpacity, {
            useNativeDriver: Platform.OS !== "web",
            duration: sortTiming.duration,
            easing: sortTiming.easing,
            toValue: 1
        });

        animation.start();

        return () => {
            animation.stop();
        };
    }, [
        sortingKey
    ]);

    if(!context) {
        return null;
    }

    const currentTheme = customThemeProp ?? context.customTheme;

    const hasSelect = context.selectionBehaviour !== "none";

    const getStickyProps = (columnIndex: number) => {
        if(columnIndex !== 0 || !hasSelect || !context.isFirstColumnSticky) {
            return {};
        }

        return {
            stickyOffset: context.selectWidth,
            isSticky: true
        };
    };

    if(context.isPermissionDenied) {
        return <DataTableState
            customTheme={currentTheme}
            kind="permission-denied"
            style={style}
        />;
    }

    const renderError = () => {
        if(!context.error) {
            return null;
        }

        return <View
            style={noticeDynamicStyle}
        >
            <Alert
                title={localize("data-table-error-title")}
                customLocalize={context.customLocalize}
                actions={context.onRetry ? [
                    {
                        title: localize("try-again"),
                        onPress: context.onRetry
                    }
                ] : undefined}
                description={context.error}
                customTheme={currentTheme}
                variant="tonal"
                type="danger"
            />
        </View>;
    };

    const renderState = () => {
        if(context.isLoading || context.error || context.rows.length) {
            return null;
        }

        return <DataTableState
            customTheme={currentTheme}
            kind="empty"
        />;
    };

    if(context.layout === "cards") {
        return <View
            style={[
                style,
                stylesheet.container
            ]}
        >
            {renderError()}
            <DataTableCards
                customTheme={currentTheme}
            />
            {renderState()}
        </View>;
    }

    const getColumnProps = (columnId: string) => {
        const column = context.columns.find((item) => item.id === columnId);

        return column ? getDataTableColumnProps({
            width: context.columnWidths[columnId],
            column
        }) : {};
    };

    const renderSkeletonRows = () => {
        return Array.from({
            length: DATA_TABLE_SKELETON_ROW_COUNT
        }, (_, rowIndex) => <Table.Row
            key={rowIndex}
        >
            {
                hasSelect ?
                    <Table.Cell
                        style={selectDynamicStyle}
                        isShrink={true}
                    />
                :
                    null
            }
            {
                context.columns.map((column, columnIndex) => <Table.Cell
                    {...getColumnProps(column.id)}
                    {...getStickyProps(columnIndex)}
                    key={column.id}
                >
                    <Skeleton
                        width={DATA_TABLE_SKELETON_WIDTHS[rowIndex % DATA_TABLE_SKELETON_WIDTHS.length]}
                        typography={TABLE_BODY_VARIANTS[context.size]}
                        customTheme={currentTheme}
                        variant="text"
                    />
                </Table.Cell>)
            }
        </Table.Row>);
    };

    const renderRows = () => {
        return context.rows.map((row) => <DataTableRow
            isExpanded={row.isExpandable && context.expandedRowIds.includes(row.id)}
            isSelected={selectedSet.has(row.id)}
            customTheme={currentTheme}
            baseId={baseId}
            key={row.id}
            row={row}
        />);
    };

    return <View
        style={[
            style,
            stylesheet.container
        ]}
    >
        {renderError()}
        <Table
            isFirstColumnSticky={context.isFirstColumnSticky}
            isHeaderSticky={context.isHeaderSticky}
            aria-busy={context.isLoading}
            customTheme={currentTheme}
            caption={context.caption}
            isCaptionHidden={true}
            size={context.size}
            isRaised={true}
        >
            <Table.Header>
                <Table.Row>
                    {
                        hasSelect ?
                            <DataTableSelectColumn
                                customTheme={currentTheme}
                            />
                        :
                            null
                    }
                    {
                        context.columns.map((column, columnIndex) => <DataTableColumnHeader
                            {...getStickyProps(columnIndex)}
                            customTheme={currentTheme}
                            columnId={column.id}
                            key={column.id}
                        />)
                    }
                </Table.Row>
            </Table.Header>
            <Table.Body
                style={{
                    opacity: rowsOpacity
                }}
            >
                {context.isLoading ? renderSkeletonRows() : renderRows()}
            </Table.Body>
        </Table>
        {renderState()}
    </View>;
};
export default memo(DataTableContent);
